import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import type { CharacterFootstep } from '../render/characters/footsteps';
import type { ParticleService } from '../vehicles/fx/particles';
import { weatherState } from './ambientFx';
import type { Weather } from './ambientFx';

/**
 * Footstep fx: subtle dust puffs when running on dry ground, small splashes + ripples when it is
 * wet. Consumes the `characterFootsteps` bus (view-layer only) and emits into the pooled particle
 * service. `decideFootstepFx` is the pure spawn decision (unit-tested); the system adds the rate cap.
 */
export const RUN_SPEED = 4.5; // m/s: below this dry steps raise no dust
export const WET_MIN_SPEED = 1.2; // m/s: standing still doesn't splash
export const MIN_INTERVAL = 0.07; // s between spawning steps (per system)
export const MAX_PER_SECOND = 12; // hard cap on spawning steps per rolling second
export const NEAR_RANGE = 30; // m: non-player steps further from the camera focus are skipped

export type FootstepFxKind = 'none' | 'dust' | 'splash';

export interface FootstepFxDecision {
  kind: FootstepFxKind;
  count: number;
  intensity: number;
}

const NONE: FootstepFxDecision = { kind: 'none', count: 0, intensity: 0 };

/** What (if anything) a planted foot should spawn. `intensity` 0..1 as published by the rig. */
export function decideFootstepFx(weather: Weather, speed: number, intensity: number): FootstepFxDecision {
  const k = Math.min(1, Math.max(0, intensity));
  if (weather !== 'clear') {
    // Drizzle wets the ground but only shows when moving briskly; rain splashes even on a walk.
    const min = weather === 'rain' ? WET_MIN_SPEED : 2.5;
    if (speed < min) return NONE;
    return { kind: 'splash', count: 2 + Math.round(k * 3), intensity: 0.5 + k * 0.5 };
  }
  if (speed < RUN_SPEED) return NONE;
  return { kind: 'dust', count: 1 + Math.round(k * 2), intensity: 0.4 + k * 0.6 };
}

/** Rolling rate limiter: at most `MAX_PER_SECOND` spawns per second and `MIN_INTERVAL` apart. */
export class StepRateLimiter {
  private readonly stamps = new Float64Array(MAX_PER_SECOND);
  private head = 0;
  private last = -Infinity;
  allow(now: number): boolean {
    if (now - this.last < MIN_INTERVAL) return false;
    // `stamps[head]` is the oldest of the last MAX_PER_SECOND spawns.
    if (this.stamps[this.head]! > 0 && now - this.stamps[this.head]! < 1) return false;
    this.stamps[this.head] = now + 1e-9; // keep strictly positive even at t = 0
    this.head = (this.head + 1) % MAX_PER_SECOND;
    this.last = now;
    return true;
  }
}

export function createFootstepFxSystem(): System {
  let ctx: GameContext;
  let particles: ParticleService | undefined;
  let off: (() => void) | undefined;
  let clock = 0;
  let wasKnocked = false;
  const limiter = new StepRateLimiter();
  const at = { x: 0, y: 0.05, z: 0 };

  return {
    id: 'footstep-fx',
    order: 736,
    start() {
      const c = ctx;
      particles = c.services.tryGet('particles');
      const bus = c.services.tryGet('characterFootsteps');
      if (!bus || !particles) return;
      const rig = c.services.tryGet('cameraRig');
      off = bus.subscribe((s: Readonly<CharacterFootstep>) => {
        const d = decideFootstepFx(weatherState.current, s.speed, s.intensity);
        if (d.kind === 'none') return;
        if (s.source !== 'player' && rig && Math.hypot(s.x - rig.lookAt.x, s.z - rig.lookAt.z) > NEAR_RANGE) return;
        if (!limiter.allow(clock)) return;
        at.x = s.x;
        at.z = s.z;
        if (d.kind === 'dust') {
          particles!.emit('dust', at, { count: d.count, intensity: d.intensity * 0.45, color: 0x8a8272 });
        } else {
          particles!.emit('splash', at, { count: d.count, intensity: d.intensity });
          particles!.emit('ripple', at, { count: 1, intensity: d.intensity });
        }
      });
    },
    init(c) {
      ctx = c;
    },
    update(dt) {
      clock += dt;
      // Light camera nudge when the player hits the ground after a knockdown.
      const knocked = ctx.services.tryGet('player')?.mode === 'knocked';
      if (knocked && !wasKnocked) ctx.services.tryGet('cameraRig')?.shake(0.14);
      wasKnocked = knocked;
    },
    dispose() {
      off?.();
    },
  };
}
